import React, { useState, useEffect, useContext } from 'react';
import PositionHeader from './PositionHeader';
import RichTextEditor from '../common/RichTextEditor';
import ConfirmationDialog from '../utils/ConfirmationDialog';
import { ApiContext } from '../../App';

const InfosPoste = ({ position, onUpdate, onDelete }) => {
  const API = useContext(ApiContext);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(position);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Extraire l'ID du manager si c'est un objet
    const formattedData = {
      ...position,
      manager: position.manager?._id || position.manager || '',
      // Convertir les valeurs numériques 0 ou null en '' pour les inputs
      experience: position.experience || '',
      bonus: position.bonus || ''
    };
    setFormData(formattedData);
  }, [position]);

  // Charger la liste des utilisateurs pour le dropdown du manager
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await API.get('/api/user/company');
        setUsers(response.data);
      } catch (error) {
        console.error('❌ Error fetching users:', error);
        console.error('Error details:', error.response?.data);
      }
    };
    if (isEditing) {
      fetchUsers();
    }
  }, [isEditing, API]);

  const handleEdit = (e) => {
    e.preventDefault();
    const updatedData = { ...formData };

    if (typeof updatedData.skills === 'string') {
      updatedData.skills = updatedData.skills.split(',').map(skill => skill.trim()).filter(skill => skill);
    }

    if (typeof updatedData.languages === 'string') {
      updatedData.languages = updatedData.languages.split(',').map(language => language.trim()).filter(language => language);
    }

    // Nettoyer le champ manager : convertir "" en null pour les ObjectId
    if (updatedData.manager === '') {
      updatedData.manager = null;
    }

    // Nettoyer les champs numériques : convertir "" en null, mais garder 0 si intentionnel
    // Convertir en nombre ou null
    updatedData.experience = updatedData.experience === '' ? null : updatedData.experience;
    updatedData.bonus = updatedData.bonus === '' ? null : updatedData.bonus;

    onUpdate(updatedData);
    setIsEditing(false);
  };

  const toggleOpenStatus = () => {
    const updatedData = { 
      ...position,
      open: position.open === undefined ? true : !position.open 
    };
    onUpdate(updatedData);
  };

  const skillsArray = Array.isArray(position.skills) 
    ? position.skills 
    : (position.skills || '').toString().split(',').map(skill => skill.trim()).filter(skill => skill);

  const languagesArray = Array.isArray(position.languages) 
    ? position.languages 
    : (position.languages || '').toString().split(',').map(language => language.trim()).filter(language => language);

  if (isEditing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header simple */}
        <div className="bg-gray-50 px-6 py-4 border-b rounded-t-lg border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <i className="bi bi-pencil-square text-gray-600 mr-2"></i>
              <h2 className="text-lg font-semibold text-gray-900 m-0">Modifier le poste</h2>
            </div>
          </div>
        </div>

        <form onSubmit={handleEdit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Colonne gauche */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre du poste *</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: Développeur Full Stack Senior"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expérience (années)</label>
                  <input
                    type="number"
                    value={formData.experience || ''}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
                  <input
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Paris, France"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Formation</label>
                  <input
                    type="text"
                    value={formData.education || ''}
                    onChange={(e) => setFormData({ ...formData, education: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Master, Bac+5, etc."
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
                    <select
                      value={formData.contractType || ''}
                      onChange={(e) => setFormData({ ...formData, contractType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sélectionner</option>
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="Freelance">Freelance</option>
                      <option value="Stage">Stage</option>
                      <option value="Alternance">Alternance</option>
                    </select>
                </div>
              </div>

              

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salaire</label>
                  <input
                    type="text"
                    value={formData.salary || ''}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="45-55k €"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={formData.status || ''}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner</option>
                    <option value="prioritaire">Prioritaire</option>
                    <option value="important">Important</option>
                    <option value="moyen">Moyen</option>
                    <option value="pas urgent">Pas urgent</option>
                    <option value="stand by">Stand by</option>
                    <option value="recruté">Recruté</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsable du poste</label>
                  <select
                    value={formData.manager || ''}
                    onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Aucun responsable</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prime de recrutement (€)</label>
                  <input
                    type="number"
                    value={formData.bonus || ''}
                    onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="5000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Compétences (séparées par des virgules)</label>
                  <input
                    type="text"
                    value={Array.isArray(formData.skills) ? formData.skills.join(', ') : formData.skills || ''}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="React, Node.js, MongoDB"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Langues (séparées par des virgules)</label>
                  <input
                    type="text"
                    value={Array.isArray(formData.languages) ? formData.languages.join(', ') : formData.languages || ''}
                    onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Français, Anglais"
                  />
                </div>
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lien de candidature</label>
                <input
                  type="url"
                  value={formData.applicationLink || ''}
                  onChange={(e) => setFormData({ ...formData, applicationLink: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Colonne droite */}
            <div className="space-y-4">
              <div className=''>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description du poste</label>
                <div className='rounded-lg border'>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Décrivez le poste, les responsabilités, les missions..."
                    minHeight="250px"
                  />
                </div>
                
              </div>

              <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                <label className="flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={formData.open === undefined ? true : formData.open}
                    onChange={(e) => setFormData({ ...formData, open: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" 
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-700">Poste ouvert</span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button 
              type="button" 
              onClick={() => setIsEditing(false)} 
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg rounded-lg  border-gray-200">
      {/* Header utilisant le composant unifié */}
      <PositionHeader
        positionData={position}
        onEditClick={() => setIsEditing(true)}
        showEditButton={true}
        onDeleteClick={() => setShowDeleteDialog(true)}
        showDeleteButton={true}
      />
    
      {/* Informations supplémentaires spécifiques à InfosPoste */}
        
      
      {/* Contenu */}
      <div className="lg:p-10 p-6 sm:p-4 border bg-white rounded-lg mt-2 space-y-4 sm:space-y-6">
        {/* Informations principales - responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ">
            {position.location && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                  <i className="bi bi-geo-alt mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Localisation :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="truncate">{position.location}</span>
                </div>
              </div>
            )}
            {position.contractType && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                <i className="bi bi-briefcase mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Contrat :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="truncate">{position.contractType}</span>
                </div>
              </div>
            )}
            {position.salary && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                  <i className="bi bi-currency-euro mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Package :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="truncate">{position.salary}</span>
                </div>
              </div>
            )}

            {position.experience != null && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                  <i className="bi bi-award mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Expérience :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="text-gray-700 text-xs font-medium truncate">
                    {position.experience} {parseInt(position.experience) === 1 ? 'an' : 'ans'} d'exp.
                  </span>
                </div>
              </div>
            )}

            {position.status && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                <i className="bi bi-info-circle mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Status :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="truncate">{position.status}</span>
                </div>
              </div>
            )}

            {position.education && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                <i className="bi bi-mortarboard mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Formation :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="text-gray-700 text-xs font-medium truncate">
                    {position.education}
                  </span>
                </div>
              </div>
            )}

            {position.manager && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                <i className="bi bi-person-badge mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Responsable :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="text-gray-700 text-xs font-medium truncate">
                    {position.manager.name}
                  </span>
                </div>
              </div>
            )}

            {position.bonus != null && (
              <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2'>
                <i className="bi bi-cash-coin mr-1 text-blue flex-shrink-0"></i>
                <h4 className='text-sm font-medium text-gray-700 flex-shrink-0 m-0'>Prime :</h4>
                <div className="flex items-center text-gray-700 px-2 py-1 rounded text-xs font-medium">
                  <span className="text-gray-700 text-xs font-medium truncate">
                    {position.bonus} €
                  </span>
                </div>
              </div>
            )}
          </div>
        
        {/* Description */}
        {position.description && (
          <div className="bg-white border-t pt-4">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Description</h4>
            <div className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-3 rounded-md text-gray-700 leading-relaxed">
              <div
                className="text-sm sm:text-base prose prose-sm sm:prose max-w-none"
                dangerouslySetInnerHTML={{ __html: position.description }}
              />
            </div>
          </div>
        )}
        
        {/* Compétences */}
        {skillsArray.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <h4 className="text-sm sm:text-base font-medium text-gray-700 mb-2">Compétences requises</h4>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {skillsArray.map((skill, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
        </div>
        

        {/* Langues */}
        {languagesArray.length > 0 && (
          <div>
            <h4 className="text-sm sm:text-base font-medium text-gray-700 mb-2">Langues </h4>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {languagesArray.map((language, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-700 px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium"
                >
                  {language}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Lien de candidature */}
        {position.applicationLink && (
          <div className="mb-4 sm:mb-6">
            <a
              href={position.applicationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base"
            >
              <i className="bi bi-arrow-up-right-square mr-1 sm:mr-2"></i>
              <span className="hidden xs:inline">Postuler à cette offre</span>
              <span className="xs:hidden">Postuler</span>
            </a>
          </div>
        )}

      </div>

      {/* Actions spécifiques à InfosPoste */}
      <div className="px-4 sm:px-6 py-3 sm:py-4  border-gray-200">
        <div className="flex  xs:flex-row items-start xs:items-center justify-between gap-3 ">
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="inline-flex items-center py-2 text-red-600 hover:text-red-800 font-medium text-sm"
          >
            <i className="bi bi-trash mr-1"></i>
            <span className="">Supprimer</span>
          </button>

          <button
            onClick={toggleOpenStatus}
            className={`inline-flex items-center px-2 sm:px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
              position.open === false
                ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
            }`}
          >
            <i className={`bi ${position.open === false ? 'bi-play-circle' : 'bi-pause-circle'} mr-1`}></i>
            {position.open === false ? 'Rouvrir' : 'Fermer'}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={onDelete}
        title="Supprimer le poste"
        message={`Êtes-vous sûr de vouloir supprimer le poste "${position.title}" ? Cette action est irréversible et supprimera également tous les candidats associés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default InfosPoste;